import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  IndianRupee, TrendingUp, Users, Target, Clock,
  CheckCircle, AlertCircle, Loader2, CalendarDays, 
  Filter, RefreshCcw, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

const formatCurrency = (value) => {
  if (!value) return '₹0';
  return `₹${value.toLocaleString('en-IN')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '-';
  }
};

const MyIncentivePage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summaryPeriod, setSummaryPeriod] = useState('monthly');
  const [summaryData, setSummaryData] = useState(null);
  const [detailedData, setDetailedData] = useState(null);
  const [detailedLoading, setDetailedLoading] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    month: new Date().toISOString().slice(0, 7), // YYYY-MM
    client_code: '',
    meeting_type: 'all',
    status: 'all'
  });

  useEffect(() => {
    fetchSummary();
    fetchDetailed();
  }, [summaryPeriod, token]);

  useEffect(() => {
    fetchDetailed();
  }, [filters]);

  const fetchSummary = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/incentives/my-incentives?period=${summaryPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        setSummaryData(result);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailed = async () => {
    if (!token) return;
    setDetailedLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.month) params.append('month', filters.month);
      if (filters.client_code) params.append('client_code', filters.client_code);
      if (filters.meeting_type !== 'all') params.append('meeting_type', filters.meeting_type);
      if (filters.status !== 'all') params.append('status', filters.status);

      const res = await fetch(`${API_URL}/incentives/my-incentives/detailed?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        setDetailedData(result);
      }
    } catch (err) {
      console.error('Failed to fetch detailed data:', err);
    } finally {
      setDetailedLoading(false);
    }
  };

  const periodLabels = {
    daily: 'Today',
    weekly: 'This Week',
    monthly: 'This Month',
    all: 'All Time'
  };

  const getIncentiveTypeBadge = (type) => {
    const config = {
      regular_meeting: { color: 'bg-emerald-600', label: 'Regular Meeting' },
      dormant_meeting: { color: 'bg-amber-600', label: 'Dormant Meeting' },
      dormant_activation: { color: 'bg-[#C5A059]', label: 'Activation Bonus' }
    };
    const c = config[type] || { color: 'bg-slate-600', label: type };
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-600', icon: <Clock className="w-3 h-3" />, label: 'Pending' },
      activated: { color: 'bg-emerald-600', icon: <CheckCircle className="w-3 h-3" />, label: 'Activated' },
      paid: { color: 'bg-blue-600', icon: <CheckCircle className="w-3 h-3" />, label: 'Paid' }
    };
    const c = config[status] || config.paid;
    return (
      <Badge className={c.color}>
        {c.icon}
        <span className="ml-1">{c.label}</span>
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-incentive-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <IndianRupee className="w-6 h-6 text-[#C5A059]" />
            My Incentives
          </h1>
          <p className="text-slate-400 mt-1">Track your meeting and activation incentives</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={summaryPeriod} onValueChange={setSummaryPeriod}>
            <SelectTrigger className="w-32 bg-slate-800 border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_trading_day">Last Trading Day</SelectItem>
              <SelectItem value="weekly">This Week</SelectItem>
              <SelectItem value="monthly">This Month</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => { fetchSummary(); fetchDetailed(); }} className="border-[#C5A059]/30">
            <RefreshCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Incentive */}
        <Card className="bg-gradient-to-br from-[#C5A059]/20 to-[#C5A059]/5 border-[#C5A059]/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#C5A059]">Total Earned ({periodLabels[summaryPeriod]})</p>
                <p className="text-3xl font-bold text-white mt-1">
                  {formatCurrency(summaryData?.total_incentive || 0)}
                </p>
              </div>
              <IndianRupee className="w-10 h-10 text-[#C5A059] opacity-50" />
            </div>
          </CardContent>
        </Card>

        {/* Regular Meetings */}
        <Card className="bg-emerald-500/10 border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-300">Regular Meetings</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {summaryData?.breakdown?.regular_meetings?.count || 0}
                </p>
                <p className="text-sm text-emerald-400">
                  {formatCurrency(summaryData?.breakdown?.regular_meetings?.amount || 0)}
                </p>
              </div>
              <Users className="w-8 h-8 text-emerald-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        {/* Dormant Meetings */}
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-300">Dormant Meetings</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {summaryData?.breakdown?.dormant_meetings?.count || 0}
                </p>
                <p className="text-sm text-amber-400">
                  {formatCurrency(summaryData?.breakdown?.dormant_meetings?.amount || 0)}
                </p>
              </div>
              <Target className="w-8 h-8 text-amber-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        {/* Activations */}
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-300">Activations</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {summaryData?.activated_clients?.length || summaryData?.breakdown?.dormant_activations?.count || 0}
                </p>
                <p className="text-sm text-purple-400">
                  {formatCurrency(summaryData?.breakdown?.dormant_activations?.amount || 0)}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Brokerage Incentive Section */}
      {summaryData?.breakdown?.brokerage_incentive && (
        <Card className="bg-gradient-to-r from-emerald-500/10 to-[#C5A059]/10 border-emerald-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              Brokerage Incentive ({periodLabels[summaryPeriod]})
            </CardTitle>
            <CardDescription className="text-slate-400">
              {summaryData.breakdown.brokerage_incentive.rate}% of excess brokerage above target
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Target */}
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-400 mb-1">Target</p>
                <p className="text-xl font-bold text-white">
                  {formatCurrency(summaryData.breakdown.brokerage_incentive.target || 0)}
                </p>
              </div>
              
              {/* Actual */}
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-400 mb-1">Actual</p>
                <p className="text-xl font-bold text-emerald-400">
                  {formatCurrency(summaryData.breakdown.brokerage_incentive.actual || 0)}
                </p>
              </div>
              
              {/* Progress */}
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-400 mb-1">Progress</p>
                <p className={`text-xl font-bold ${
                  (summaryData.breakdown.brokerage_incentive.progress || 0) >= 100 
                    ? 'text-emerald-400' 
                    : 'text-amber-400'
                }`}>
                  {summaryData.breakdown.brokerage_incentive.progress || 0}%
                </p>
              </div>
              
              {/* Excess */}
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-400 mb-1">Excess</p>
                <p className={`text-xl font-bold ${
                  (summaryData.breakdown.brokerage_incentive.excess || 0) > 0 
                    ? 'text-emerald-400' 
                    : 'text-slate-500'
                }`}>
                  {formatCurrency(summaryData.breakdown.brokerage_incentive.excess || 0)}
                </p>
              </div>
              
              {/* Incentive Earned */}
              <div className="bg-[#C5A059]/20 rounded-lg p-4 text-center border border-[#C5A059]/30">
                <p className="text-xs text-[#C5A059] mb-1">Incentive Earned</p>
                <p className="text-xl font-bold text-[#C5A059]">
                  {formatCurrency(summaryData.breakdown.brokerage_incentive.incentive || 0)}
                </p>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs text-slate-400 mb-1">
                <span>Progress to Target</span>
                <span>{summaryData.breakdown.brokerage_incentive.progress || 0}%</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ${
                    (summaryData.breakdown.brokerage_incentive.progress || 0) >= 100 
                      ? 'bg-emerald-500' 
                      : 'bg-amber-500'
                  }`}
                  style={{ width: `${Math.min(summaryData.breakdown.brokerage_incentive.progress || 0, 100)}%` }}
                />
              </div>
              {(summaryData.breakdown.brokerage_incentive.progress || 0) >= 100 && (
                <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Target achieved! Earning {summaryData.breakdown.brokerage_incentive.rate}% on excess brokerage.
                </p>
              )}
              {(summaryData.breakdown.brokerage_incentive.progress || 0) < 100 && (
                <p className="text-xs text-slate-400 mt-2">
                  {formatCurrency((summaryData.breakdown.brokerage_incentive.target || 0) - (summaryData.breakdown.brokerage_incentive.actual || 0))} more to reach target
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Activations Alert */}
      {summaryData?.pending_activations?.length > 0 && (
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-yellow-300 flex items-center gap-2 text-base">
              <Clock className="w-5 h-5" />
              Pending Activations ({summaryData.pending_activations.length})
            </CardTitle>
            <CardDescription className="text-yellow-200/70">
              Meeting completed, awaiting brokerage within activation window
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {summaryData.pending_activations.slice(0, 6).map((p, idx) => {
                const deadline = new Date(p.deadline_date);
                const today = new Date();
                const daysLeft = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
                
                return (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-white/5"
                  >
                    <div>
                      <p className="text-sm text-white font-medium">
                        {p.client_name || p.client_code}
                        {p.client_name && <span className="text-slate-500 ml-1">({p.client_code})</span>}
                      </p>
                      <p className="text-xs text-slate-400">
                        Meeting: {formatDate(p.meeting_date)}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      daysLeft <= 7 ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                      daysLeft <= 14 ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                      'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    }`} data-testid={`pending-days-badge-${idx}`}>
                      {daysLeft} days left
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activated Clients (Dormant 3 only — meeting + brokerage completed) */}
      {summaryData?.activated_clients?.length > 0 && (
        <Card className="bg-emerald-500/10 border-emerald-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-emerald-300 flex items-center gap-2 text-base">
              <CheckCircle className="w-5 h-5" />
              Recently Activated Clients ({summaryData.activated_clients.length})
            </CardTitle>
            <CardDescription className="text-emerald-200/70">
              Dormant 3 clients reactivated via meeting + brokerage
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {summaryData.activated_clients.slice(0, 6).map((a, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-white/5"
                >
                  <div>
                    <p className="text-sm text-white font-medium">
                      {a.client_name || a.client_code}
                      {a.client_name && <span className="text-slate-500 ml-1">({a.client_code})</span>}
                    </p>
                    <p className="text-xs text-slate-400">
                      Activated: {formatDate(a.activation_date)}
                    </p>
                    {a.brokerage_amount > 0 && (
                      <p className="text-xs text-slate-500">
                        Brokerage: {formatCurrency(a.brokerage_amount)}
                      </p>
                    )}
                  </div>
                  <span className="text-emerald-400 font-bold">
                    +{formatCurrency(a.incentive_amount || 0)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Table */}
      <Card className="card-dark border-white/5">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Incentive Details</CardTitle>
              <CardDescription>
                {detailedData?.count || 0} records found
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Filters</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 p-4 bg-slate-800/50 rounded-lg">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Month</label>
              <Input
                type="month"
                value={filters.month}
                onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                className="bg-slate-800 border-white/10"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Client Code</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={filters.client_code}
                  onChange={(e) => setFilters({ ...filters, client_code: e.target.value })}
                  className="pl-10 bg-slate-800 border-white/10"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Meeting Type</label>
              <Select value={filters.meeting_type} onValueChange={(v) => setFilters({ ...filters, meeting_type: v })}>
                <SelectTrigger className="bg-slate-800 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="dormant">Dormant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Status</label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger className="bg-slate-800 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="activated">Activated</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Totals Row */}
          {detailedData?.totals && (
            <div className="grid grid-cols-4 gap-2 mb-4 p-3 bg-[#C5A059]/10 rounded-lg border border-[#C5A059]/20">
              <div className="text-center">
                <p className="text-xs text-slate-400">Regular</p>
                <p className="text-sm font-bold text-emerald-400">
                  {formatCurrency(detailedData.totals.regular_meetings?.amount || 0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-400">Dormant</p>
                <p className="text-sm font-bold text-amber-400">
                  {formatCurrency(detailedData.totals.dormant_meetings?.amount || 0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-400">Activations</p>
                <p className="text-sm font-bold text-purple-400">
                  {formatCurrency(detailedData.totals.dormant_activations?.amount || 0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-400">Total</p>
                <p className="text-sm font-bold text-[#C5A059]">
                  {formatCurrency(detailedData.totals.total || 0)}
                </p>
              </div>
            </div>
          )}

          {/* Table */}
          {detailedLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" />
            </div>
          ) : (
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-slate-400">Client Code</TableHead>
                    <TableHead className="text-slate-400">Client Name</TableHead>
                    <TableHead className="text-slate-400">Meeting Type</TableHead>
                    <TableHead className="text-slate-400">Meeting Date</TableHead>
                    <TableHead className="text-slate-400">Activation Date</TableHead>
                    <TableHead className="text-slate-400 text-right">Incentive</TableHead>
                    <TableHead className="text-slate-400 text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailedData?.incentives?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                        <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No incentive records found</p>
                        <p className="text-xs mt-1">Log client meetings to earn incentives!</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    detailedData?.incentives?.map((inc, idx) => (
                      <TableRow key={idx} className="border-white/5 hover:bg-white/5">
                        <TableCell>
                          <code className="text-[#C5A059] font-mono text-sm bg-[#C5A059]/10 px-2 py-1 rounded">
                            {inc.client_code}
                          </code>
                        </TableCell>
                        <TableCell className="text-white">{inc.client_name || '-'}</TableCell>
                        <TableCell>{getIncentiveTypeBadge(inc.incentive_type)}</TableCell>
                        <TableCell className="text-slate-300">{formatDate(inc.meeting_date)}</TableCell>
                        <TableCell className="text-slate-300">
                          {inc.incentive_type === 'dormant_activation' ? formatDate(inc.brokerage_date) : '-'}
                        </TableCell>
                        <TableCell className="text-right text-[#C5A059] font-bold">
                          {formatCurrency(inc.incentive_amount)}
                        </TableCell>
                        <TableCell className="text-center">{getStatusBadge(inc.status)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyIncentivePage;
